import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  UserPlus,
  GraduationCap,
  IndianRupee,
  Calendar,
  User,
  Mail,
  Phone,
  MapPin,
  BookOpen,
  AlertCircle,
  Sparkles,
  Calculator,
} from "lucide-react";
import {
  studentService,
  CourseGroupForEnrollment,
  YearWiseFee,
} from "@/services/studentService";
import YearwiseFeeBreakdown from "./YearwiseFeeBreakdown";
import YearWiseDiscountInput, {
  YearWiseDiscount,
} from "./YearWiseDiscountInput";
import type { RootState } from "@/store";
import { formatCurrency } from "@/lib/model";

interface AddStudentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStudentAdded: () => void;
  student?: any; // For edit mode
  mode?: "add" | "edit";
}

// Generate current academic year (e.g., "2024-25")
const getCurrentAcademicYear = (): string => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed

  // Academic year typically starts in June
  if (currentMonth >= 6) {
    return `${currentYear}-${(currentYear + 1).toString().slice(-2)}`;
  } else {
    return `${currentYear - 1}-${currentYear.toString().slice(-2)}`;
  }
};

// Generate current batch year (e.g., "2024")
const getCurrentBatchYear = (): string => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // If we're in the admission period (March-August), use current year
  // Otherwise, use next year for new admissions
  if (currentMonth >= 3 && currentMonth <= 8) {
    return currentYear.toString();
  } else {
    return (currentYear + 1).toString();
  }
};

// Generate username from email (part before @)
const generateUsernameFromEmail = (email: string): string => {
  return email.split("@")[0].toLowerCase();
};

// Generate a default password
const generateDefaultPassword = (): string => {
  return "Student@123"; // Default password that meets backend requirements
};

// Validation helper
const validateForm = (
  formData: any,
  yearWiseDiscounts: YearWiseDiscount[]
): string[] => {
  const errors: string[] = [];

  if (!formData.firstName?.trim()) errors.push("First name is required");
  if (!formData.lastName?.trim()) errors.push("Last name is required");
  if (!formData.email?.trim()) errors.push("Email is required");
  if (!formData.courseGroupId) errors.push("Course group is required");
  if (!formData.dob) errors.push("Date of birth is required");

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (formData.email && !emailRegex.test(formData.email)) {
    errors.push("Please enter a valid email address");
  }

  // Date of birth validation (should be in the past and reasonable age)
  if (formData.dob) {
    const birthDate = new Date(formData.dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();

    if (birthDate >= today) {
      errors.push("Date of birth must be in the past");
    } else if (age < 15 || age > 40) {
      errors.push("Student age should be between 15 and 40 years");
    }
  }

  // Year-wise discount validation
  const selectedCourseGroup = formData.selectedCourseGroup;

  if (yearWiseDiscounts.length > 0 && selectedCourseGroup?.yearWiseFees) {
    yearWiseDiscounts.forEach((discount) => {
      const discountAmount = parseFloat(discount.amount || "0");

      if (discountAmount < 0) {
        errors.push(
          `Discount amount for Year ${discount.year} cannot be negative`
        );
      }

      // Find the corresponding year fee
      const yearFee = selectedCourseGroup.yearWiseFees.find(
        (fee: YearWiseFee) => fee.yearNumber === discount.year
      );

      if (yearFee && discountAmount > yearFee.feeAmount) {
        errors.push(
          `Discount for Year ${
            discount.year
          } (₹${discountAmount.toLocaleString()}) cannot exceed year fee of ₹${yearFee.feeAmount.toLocaleString()}`
        );
      }

      if (discountAmount > 0 && !discount.reason?.trim()) {
        errors.push(`Discount reason is required for Year ${discount.year}`);
      }
    });
  }

  return errors;
};

const AddStudentDialog: React.FC<AddStudentDialogProps> = ({
  open,
  onOpenChange,
  onStudentAdded,
  student,
  mode = "add",
}) => {
  // Get current user from auth state
  const { user: currentUser } = useSelector((state: RootState) => state.auth);

  const [courseGroups, setCourseGroups] = useState<CourseGroupForEnrollment[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [loadingCourseGroups, setLoadingCourseGroups] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const isEditMode = mode === "edit" && student;

  const [formData, setFormData] = useState({
    // Personal Information
    firstName: isEditMode ? student.firstName || "" : "",
    lastName: isEditMode ? student.lastName || "" : "",
    email: isEditMode ? student.email || "" : "",
    dob: isEditMode ? (student.dob ? student.dob.split("T")[0] : "") : "",
    gender: isEditMode ? student.gender || "" : "",
    fatherName: isEditMode ? student.fatherName || "" : "",
    motherName: isEditMode ? student.motherName || "" : "",
    guardianName: isEditMode ? student.guardianName || "" : "",
    aadharNumber: isEditMode ? student.aadharNumber || "" : "",
    caste: isEditMode ? student.caste || "" : "",
    category: isEditMode ? student.category || "" : "",
    religion: isEditMode ? student.religion || "" : "",
    nationality: isEditMode ? student.nationality || "" : "",
    secondLanguageName: isEditMode ? student.secondLanguageName || "" : "",
    mole1: isEditMode ? student.mole1 || "" : "",
    mole2: isEditMode ? student.mole2 || "" : "",

    // Contact Information
    studentPhone: isEditMode ? student.studentPhone || "" : "",
    fatherPhone: isEditMode ? student.fatherPhone || "" : "",
    motherPhone: isEditMode ? student.motherPhone || "" : "",
    guardianPhone: isEditMode ? student.guardianPhone || "" : "",
    address: isEditMode ? student.address || "" : "",
    town: isEditMode ? student.town || "" : "",
    district: isEditMode ? student.district || "" : "",
    state: isEditMode ? student.state || "" : "",
    pincode: isEditMode ? student.pincode || "" : "",

    // Academic Information - Enhanced
    intermediateGroup: isEditMode ? student.intermediateGroup || "" : "",
    intermediateGroupOther: isEditMode
      ? student.intermediateGroupOther || ""
      : "",
    intermediateBoard: isEditMode ? student.intermediateBoard || "" : "",
    intermediateBoardOther: isEditMode
      ? student.intermediateBoardOther || ""
      : "",
    intermediateMarks: isEditMode
      ? student.intermediateMarks?.toString() || ""
      : "",
    intermediateMaxMarks: isEditMode
      ? student.intermediateMaxMarks?.toString() || ""
      : "",
    intermediatePercentage: isEditMode
      ? student.intermediatePercentage?.toString() || ""
      : "",
    intermediateYearOfPassing: isEditMode
      ? student.intermediateYearOfPassing || ""
      : "",
    intermediateAcademicYears: isEditMode
      ? student.intermediateAcademicYears || ""
      : "",
    intermediateMediumOfInstruction: isEditMode
      ? student.intermediateMediumOfInstruction || ""
      : "",
    intermediateHallTicketNumber: isEditMode
      ? student.intermediateHallTicketNumber || ""
      : "",
    intermediateCollege: isEditMode ? student.intermediateCollege || "" : "",

    // 10th Grade Information
    tenthBoard: isEditMode ? student.tenthBoard || "" : "",
    tenthBoardOther: isEditMode ? student.tenthBoardOther || "" : "",
    tenthMarks: isEditMode ? student.tenthMarks?.toString() || "" : "",
    tenthMaxMarks: isEditMode ? student.tenthMaxMarks?.toString() || "" : "",
    tenthPercentage: isEditMode
      ? student.tenthPercentage?.toString() || ""
      : "",
    tenthYearOfPassing: isEditMode ? student.tenthYearOfPassing || "" : "",
    tenthAcademicYears: isEditMode ? student.tenthAcademicYears || "" : "",
    tenthMediumOfInstruction: isEditMode
      ? student.tenthMediumOfInstruction || ""
      : "",
    tenthHallTicketNumber: isEditMode
      ? student.tenthHallTicketNumber || ""
      : "",

    medium: isEditMode ? student.medium || "" : "",

    // Enrollment Information
    courseGroupId: isEditMode ? student.courseGroup?.id || "" : "",
    selectedCourseGroup: isEditMode
      ? student.courseGroup
        ? {
            id: student.courseGroup.id,
            name: student.courseGroup.name,
            code: student.courseGroup.code,
            totalFee: student.totalFee || 0,
            formattedFee: `₹${(student.totalFee || 0).toLocaleString()}`,
            durationYears: student.courseGroup.durationYears || 4,
            description: student.courseGroup.description,
            yearWiseFees: [], // Will be populated from API if available
          }
        : null
      : (null as CourseGroupForEnrollment | null),
    academicYear: isEditMode
      ? student.academicYear || getCurrentAcademicYear()
      : getCurrentAcademicYear(),
    batchYear: isEditMode
      ? student.batchYear || getCurrentBatchYear()
      : getCurrentBatchYear(),
    enrollmentDate: isEditMode
      ? student.enrollmentDate
        ? student.enrollmentDate.split("T")[0]
        : new Date().toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],

    // Fee Information (Auto-calculated)
    totalFee: isEditMode ? student.totalFee || 0 : 0,
    finalFee: isEditMode ? student.finalFee || 0 : 0,
  });

  // Year-wise discounts state (separate from formData for better management)
  const [yearWiseDiscounts, setYearWiseDiscounts] = useState<
    YearWiseDiscount[]
  >(
    isEditMode && student.yearWiseDiscounts
      ? student.yearWiseDiscounts.map((d: any) => ({
          year: d.year,
          amount: d.amount?.toString() || "0",
          reason: d.reason || "",
        }))
      : []
  );

  // Load course groups when dialog opens
  useEffect(() => {
    if (open && courseGroups.length === 0) {
      loadCourseGroups();
    }
  }, [open]);

  // Update selectedCourseGroup with yearWiseFees when courseGroups are loaded in edit mode
  useEffect(() => {
    // Only run when dialog is open and we have all required data
    if (
      open &&
      isEditMode &&
      formData.courseGroupId &&
      courseGroups.length > 0
    ) {
      const matchingCourseGroup = courseGroups.find(
        (g) => g.id === formData.courseGroupId
      );
      
      // Update if we found a matching course group with yearWiseFees
      // and current selectedCourseGroup doesn't have them or has empty array
      if (
        matchingCourseGroup &&
        matchingCourseGroup.yearWiseFees &&
        matchingCourseGroup.yearWiseFees.length > 0
      ) {
        // Always update if the selectedCourseGroup doesn't have yearWiseFees or has empty array
        const currentYearWiseFees = formData.selectedCourseGroup?.yearWiseFees;
        const needsUpdate =
          !formData.selectedCourseGroup ||
          !currentYearWiseFees ||
          currentYearWiseFees.length === 0 ||
          formData.selectedCourseGroup.id !== matchingCourseGroup.id;

        if (needsUpdate) {
          setFormData((prev) => ({
            ...prev,
            selectedCourseGroup: matchingCourseGroup,
          }));
        }
      }
    }
  }, [open, courseGroups, isEditMode, formData.courseGroupId]);

  // Reset/initialize form data when student prop changes (for edit mode)
  useEffect(() => {
    if (isEditMode && student && open) {
      setFormData({
        // Personal Information
        firstName: student.firstName || "",
        lastName: student.lastName || "",
        email: student.email || "",
        dob: student.dob ? student.dob.split("T")[0] : "",
        gender: student.gender || "",
        fatherName: student.fatherName || "",
        motherName: student.motherName || "",
        guardianName: student.guardianName || "",
        aadharNumber: student.aadharNumber || "",
        caste: student.caste || "",
        category: student.category || "",
        religion: student.religion || "",
        nationality: student.nationality || "",
        secondLanguageName: student.secondLanguageName || "",
        mole1: student.mole1 || "",
        mole2: student.mole2 || "",

        // Contact Information
        studentPhone: student.studentPhone || "",
        fatherPhone: student.fatherPhone || "",
        motherPhone: student.motherPhone || "",
        guardianPhone: student.guardianPhone || "",
        address: student.address || "",
        town: student.town || "",
        district: student.district || "",
        state: student.state || "",
        pincode: student.pincode || "",

        // Enhanced Academic Information
        intermediateGroup: student.intermediateGroup || "",
        intermediateGroupOther: student.intermediateGroupOther || "",
        intermediateBoard: student.intermediateBoard || "",
        intermediateBoardOther: student.intermediateBoardOther || "",
        intermediateMarks: student.intermediateMarks?.toString() || "",
        intermediateMaxMarks: student.intermediateMaxMarks?.toString() || "",
        intermediatePercentage:
          student.intermediatePercentage?.toString() || "",
        intermediateYearOfPassing: student.intermediateYearOfPassing || "",
        intermediateAcademicYears: student.intermediateAcademicYears || "",
        intermediateMediumOfInstruction:
          student.intermediateMediumOfInstruction || "",
        intermediateHallTicketNumber:
          student.intermediateHallTicketNumber || "",
        intermediateCollege: student.intermediateCollege || "",

        // 10th Grade Information
        tenthBoard: student.tenthBoard || "",
        tenthBoardOther: student.tenthBoardOther || "",
        tenthMarks: student.tenthMarks?.toString() || "",
        tenthMaxMarks: student.tenthMaxMarks?.toString() || "",
        tenthPercentage: student.tenthPercentage?.toString() || "",
        tenthYearOfPassing: student.tenthYearOfPassing || "",
        tenthAcademicYears: student.tenthAcademicYears || "",
        tenthMediumOfInstruction: student.tenthMediumOfInstruction || "",
        tenthHallTicketNumber: student.tenthHallTicketNumber || "",

        medium: student.medium || "",

        // Enrollment Information
        courseGroupId: student.courseGroup?.id || "",
        selectedCourseGroup: (() => {
          // Try to find matching course group with yearWiseFees from loaded courseGroups
          if (student.courseGroup?.id && courseGroups.length > 0) {
            const matchingCourseGroup = courseGroups.find(
              (g) => g.id === student.courseGroup.id
            );
            if (
              matchingCourseGroup &&
              matchingCourseGroup.yearWiseFees &&
              matchingCourseGroup.yearWiseFees.length > 0
            ) {
              return matchingCourseGroup;
            }
          }
          // Fallback to student data if course group not found or no yearWiseFees
          return student.courseGroup
            ? {
                id: student.courseGroup.id,
                name: student.courseGroup.name,
                code: student.courseGroup.code,
                totalFee: student.totalFee || 0,
                formattedFee: `₹${(student.totalFee || 0).toLocaleString()}`,
                durationYears: student.courseGroup.durationYears || 4,
                description: student.courseGroup.description,
                yearWiseFees: [], // Will be populated from API if available
              }
            : null;
        })(),
        academicYear: student.academicYear || getCurrentAcademicYear(),
        batchYear: student.batchYear || getCurrentBatchYear(),
        enrollmentDate: student.enrollmentDate
          ? student.enrollmentDate.split("T")[0]
          : new Date().toISOString().split("T")[0],

        // Fee Information (Auto-calculated)
        totalFee: student.totalFee || 0,
        finalFee: student.finalFee || 0,
      });

      // Load year-wise discounts
      setYearWiseDiscounts(
        student.yearWiseDiscounts
          ? student.yearWiseDiscounts.map((d: any) => ({
              year: d.year,
              amount: d.amount?.toString() || "0",
              reason: d.reason || "",
            }))
          : []
      );

      // Update selectedCourseGroup with yearWiseFees if courseGroups are already loaded
      if (student.courseGroup?.id && courseGroups.length > 0) {
        const matchingCourseGroup = courseGroups.find(
          (g) => g.id === student.courseGroup.id
        );
        if (
          matchingCourseGroup &&
          matchingCourseGroup.yearWiseFees &&
          matchingCourseGroup.yearWiseFees.length > 0
        ) {
          // Use setTimeout to ensure this runs after state update
          setTimeout(() => {
            setFormData((prev) => {
              if (prev.courseGroupId === student.courseGroup.id) {
                return {
                  ...prev,
                  selectedCourseGroup: matchingCourseGroup,
                };
              }
              return prev;
            });
          }, 0);
        }
      }
    } else if (!isEditMode && open) {
      // Reset to empty form for add mode
      resetForm();
    }
  }, [isEditMode, student, open, courseGroups]);

  const loadCourseGroups = async () => {
    setLoadingCourseGroups(true);
    try {
      const groups = await studentService.getCourseGroupsForEnrollment();
      setCourseGroups(groups);
    } catch (error) {
      console.error("Error loading course groups:", error);
      setErrors(["Failed to load course groups. Please try again."]);
    } finally {
      setLoadingCourseGroups(false);
    }
  };

  // Calculate final fee when discount changes
  const calculateFinalFee = (
    yearWiseDiscounts: YearWiseDiscount[],
    totalFee: number
  ) => {
    // Ensure yearWiseDiscounts is an array
    if (!Array.isArray(yearWiseDiscounts)) {
      return totalFee;
    }

    const totalDiscount = yearWiseDiscounts.reduce(
      (sum, discount) => sum + (parseFloat(discount.amount) || 0),
      0
    );
    return Math.max(0, totalFee - totalDiscount);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleCourseGroupChange = (courseGroupId: string) => {
    const selectedGroup = courseGroups.find((g) => g.id === courseGroupId);

    setFormData((prev) => {
      // Calculate total fee from year-wise fees if available, otherwise use totalFee
      const totalFee =
        selectedGroup &&
        selectedGroup.yearWiseFees &&
        selectedGroup.yearWiseFees.length > 0
          ? selectedGroup.yearWiseFees.reduce(
              (sum, fee) => sum + fee.feeAmount,
              0
            )
          : selectedGroup?.totalFee || 0;

      return {
        ...prev,
        courseGroupId,
        selectedCourseGroup: selectedGroup || null,
        totalFee,
        finalFee: calculateFinalFee(yearWiseDiscounts, totalFee),
      };
    });

    // Reset yearWiseDiscounts when course group changes
    setYearWiseDiscounts([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateForm(formData, yearWiseDiscounts);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Validate that current user has a college (not needed for edit mode if student already exists)
    if (!isEditMode && !currentUser?.college?.id) {
      setErrors([
        "Unable to determine your college. Please contact an administrator.",
      ]);
      return;
    }

    setLoading(true);
    setErrors([]);

    try {
      if (isEditMode && student) {
        // UPDATE MODE - Update existing student
        const updateData = {
          // Personal Information
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          dob: formData.dob,
          gender: formData.gender || "Other",

          // Contact Information
          studentPhone: formData.studentPhone?.trim() || null,
          fatherPhone: formData.fatherPhone?.trim() || null,
          motherPhone: formData.motherPhone?.trim() || null,
          guardianPhone: formData.guardianPhone?.trim() || null,
          address: formData.address?.trim() || null,
          town: formData.town?.trim() || null,
          district: formData.district?.trim() || null,
          state: formData.state?.trim() || null,
          pincode: formData.pincode?.trim() || null,

          // Personal Details
          fatherName: formData.fatherName?.trim() || null,
          motherName: formData.motherName?.trim() || null,
          guardianName: formData.guardianName?.trim() || null,
          aadharNumber: formData.aadharNumber?.trim() || null,
          caste: formData.caste?.trim() || null,
          category: formData.category?.trim() || null,
          religion: formData.religion?.trim() || null,
          nationality: formData.nationality?.trim() || null,
          secondLanguageName: formData.secondLanguageName?.trim() || null,
          mole1: formData.mole1?.trim() || null,
          mole2: formData.mole2?.trim() || null,

          // Academic Information
          courseGroupId: formData.courseGroupId,
          academicYear: formData.academicYear,
          batchYear: formData.batchYear,
          enrollmentDate: formData.enrollmentDate,

          // Enhanced Academic Background
          intermediateGroup: formData.intermediateGroup?.trim() || null,
          intermediateGroupOther:
            formData.intermediateGroupOther?.trim() || null,
          intermediateBoard: formData.intermediateBoard?.trim() || null,
          intermediateBoardOther:
            formData.intermediateBoardOther?.trim() || null,
          intermediateMarks: formData.intermediateMarks
            ? parseFloat(formData.intermediateMarks)
            : null,
          intermediateMaxMarks: formData.intermediateMaxMarks
            ? parseFloat(formData.intermediateMaxMarks)
            : null,
          intermediatePercentage: formData.intermediatePercentage
            ? parseFloat(formData.intermediatePercentage)
            : null,
          intermediateYearOfPassing:
            formData.intermediateYearOfPassing?.trim() || null,
          intermediateAcademicYears:
            formData.intermediateAcademicYears?.trim() || null,
          intermediateMediumOfInstruction:
            formData.intermediateMediumOfInstruction?.trim() || null,
          intermediateHallTicketNumber:
            formData.intermediateHallTicketNumber?.trim() || null,
          intermediateCollege: formData.intermediateCollege?.trim() || null,

          // 10th Grade Education
          tenthBoard: formData.tenthBoard?.trim() || null,
          tenthBoardOther: formData.tenthBoardOther?.trim() || null,
          tenthMarks: formData.tenthMarks
            ? parseFloat(formData.tenthMarks)
            : null,
          tenthMaxMarks: formData.tenthMaxMarks
            ? parseFloat(formData.tenthMaxMarks)
            : null,
          tenthPercentage: formData.tenthPercentage
            ? parseFloat(formData.tenthPercentage)
            : null,
          tenthYearOfPassing: formData.tenthYearOfPassing?.trim() || null,
          tenthAcademicYears: formData.tenthAcademicYears?.trim() || null,
          tenthMediumOfInstruction:
            formData.tenthMediumOfInstruction?.trim() || null,
          tenthHallTicketNumber: formData.tenthHallTicketNumber?.trim() || null,

          medium: formData.medium?.trim() || null,

          // Fee Information
          totalFee: formData.totalFee,
          yearWiseDiscounts: yearWiseDiscounts
            .filter((d) => parseFloat(d.amount) > 0)
            .map((d) => ({
              year: d.year,
              amount: parseFloat(d.amount),
              reason: d.reason.trim(),
            })),
          finalFee: calculateFinalFee(yearWiseDiscounts, formData.totalFee),
        };

        await studentService.updateStudent(student.id, updateData);
      } else {
        // CREATE MODE - Create new student
        // Generate required fields
        const username = generateUsernameFromEmail(formData.email.trim());
        const password = generateDefaultPassword();

        // Prepare student data for API
        const studentData = {
          // REQUIRED FIELDS FOR BACKEND
          username,
          password,
          collegeId: currentUser!.college!.id,

          // Personal Information
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          dob: formData.dob,
          gender: formData.gender || "Other",

          // Contact Information
          studentPhone: formData.studentPhone?.trim() || null,
          fatherPhone: formData.fatherPhone?.trim() || null,
          motherPhone: formData.motherPhone?.trim() || null,
          guardianPhone: formData.guardianPhone?.trim() || null,
          address: formData.address?.trim() || null,
          town: formData.town?.trim() || null,
          district: formData.district?.trim() || null,
          state: formData.state?.trim() || null,
          pincode: formData.pincode?.trim() || null,

          // Personal Details
          fatherName: formData.fatherName?.trim() || null,
          motherName: formData.motherName?.trim() || null,
          guardianName: formData.guardianName?.trim() || null,
          aadharNumber: formData.aadharNumber?.trim() || null,
          caste: formData.caste?.trim() || null,
          category: formData.category?.trim() || null,
          religion: formData.religion?.trim() || null,
          nationality: formData.nationality?.trim() || null,
          secondLanguageName: formData.secondLanguageName?.trim() || null,
          mole1: formData.mole1?.trim() || null,
          mole2: formData.mole2?.trim() || null,

          // Academic Information
          courseGroupId: formData.courseGroupId,
          academicYear: formData.academicYear,
          batchYear: formData.batchYear,
          enrollmentDate: formData.enrollmentDate,

          // Enhanced Academic Background
          intermediateGroup: formData.intermediateGroup?.trim() || null,
          intermediateGroupOther:
            formData.intermediateGroupOther?.trim() || null,
          intermediateBoard: formData.intermediateBoard?.trim() || null,
          intermediateBoardOther:
            formData.intermediateBoardOther?.trim() || null,
          intermediateMarks: formData.intermediateMarks
            ? parseFloat(formData.intermediateMarks)
            : null,
          intermediateMaxMarks: formData.intermediateMaxMarks
            ? parseFloat(formData.intermediateMaxMarks)
            : null,
          intermediatePercentage: formData.intermediatePercentage
            ? parseFloat(formData.intermediatePercentage)
            : null,
          intermediateYearOfPassing:
            formData.intermediateYearOfPassing?.trim() || null,
          intermediateAcademicYears:
            formData.intermediateAcademicYears?.trim() || null,
          intermediateMediumOfInstruction:
            formData.intermediateMediumOfInstruction?.trim() || null,
          intermediateHallTicketNumber:
            formData.intermediateHallTicketNumber?.trim() || null,
          intermediateCollege: formData.intermediateCollege?.trim() || null,

          // 10th Grade Education
          tenthBoard: formData.tenthBoard?.trim() || null,
          tenthBoardOther: formData.tenthBoardOther?.trim() || null,
          tenthMarks: formData.tenthMarks
            ? parseFloat(formData.tenthMarks)
            : null,
          tenthMaxMarks: formData.tenthMaxMarks
            ? parseFloat(formData.tenthMaxMarks)
            : null,
          tenthPercentage: formData.tenthPercentage
            ? parseFloat(formData.tenthPercentage)
            : null,
          tenthYearOfPassing: formData.tenthYearOfPassing?.trim() || null,
          tenthAcademicYears: formData.tenthAcademicYears?.trim() || null,
          tenthMediumOfInstruction:
            formData.tenthMediumOfInstruction?.trim() || null,
          tenthHallTicketNumber: formData.tenthHallTicketNumber?.trim() || null,

          medium: formData.medium?.trim() || null,

          // Fee Information
          totalFee: formData.totalFee,
          yearWiseDiscounts: yearWiseDiscounts
            .filter((d) => parseFloat(d.amount) > 0)
            .map((d) => ({
              year: d.year,
              amount: parseFloat(d.amount),
              reason: d.reason.trim(),
            })),
          finalFee: calculateFinalFee(yearWiseDiscounts, formData.totalFee),

          // Default values
          enrollmentStatus: "active",
          profileCompleted: false,
        };

        await studentService.createStudent(studentData);
      }

      // Success! Reset form and close dialog
      resetForm();
      onOpenChange(false);
      onStudentAdded();
    } catch (error: any) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} student:`,
        error
      );
      const errorMessage =
        error.response?.data?.message ||
        `Failed to ${
          isEditMode ? "update" : "create"
        } student. Please try again.`;
      setErrors([errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    if (isEditMode && student) {
      // Reset to original student data for edit mode
      setFormData({
        firstName: student.firstName || "",
        lastName: student.lastName || "",
        email: student.email || "",
        dob: student.dob ? student.dob.split("T")[0] : "",
        gender: student.gender || "",
        fatherName: student.fatherName || "",
        motherName: student.motherName || "",
        guardianName: student.guardianName || "",
        aadharNumber: student.aadharNumber || "",
        caste: student.caste || "",
        category: student.category || "",
        religion: student.religion || "",
        nationality: student.nationality || "",
        secondLanguageName: student.secondLanguageName || "",
        mole1: student.mole1 || "",
        mole2: student.mole2 || "",
        studentPhone: student.studentPhone || "",
        fatherPhone: student.fatherPhone || "",
        motherPhone: student.motherPhone || "",
        guardianPhone: student.guardianPhone || "",
        address: student.address || "",
        town: student.town || "",
        district: student.district || "",
        state: student.state || "",
        pincode: student.pincode || "",
        intermediateGroup: student.intermediateGroup || "",
        intermediateGroupOther: student.intermediateGroupOther || "",
        intermediateBoard: student.intermediateBoard || "",
        intermediateBoardOther: student.intermediateBoardOther || "",
        intermediateMarks: student.intermediateMarks?.toString() || "",
        intermediateMaxMarks: student.intermediateMaxMarks?.toString() || "",
        intermediatePercentage:
          student.intermediatePercentage?.toString() || "",
        intermediateYearOfPassing: student.intermediateYearOfPassing || "",
        intermediateAcademicYears: student.intermediateAcademicYears || "",
        intermediateMediumOfInstruction:
          student.intermediateMediumOfInstruction || "",
        intermediateHallTicketNumber:
          student.intermediateHallTicketNumber || "",
        intermediateCollege: student.intermediateCollege || "",
        tenthBoard: student.tenthBoard || "",
        tenthBoardOther: student.tenthBoardOther || "",
        tenthMarks: student.tenthMarks?.toString() || "",
        tenthMaxMarks: student.tenthMaxMarks?.toString() || "",
        tenthPercentage: student.tenthPercentage?.toString() || "",
        tenthYearOfPassing: student.tenthYearOfPassing || "",
        tenthAcademicYears: student.tenthAcademicYears || "",
        tenthMediumOfInstruction: student.tenthMediumOfInstruction || "",
        tenthHallTicketNumber: student.tenthHallTicketNumber || "",

        medium: student.medium || "",
        courseGroupId: student.courseGroup?.id || "",
        selectedCourseGroup: student.courseGroup
          ? {
              id: student.courseGroup.id,
              name: student.courseGroup.name,
              code: student.courseGroup.code,
              totalFee: student.totalFee || 0,
              formattedFee: `₹${(student.totalFee || 0).toLocaleString()}`,
              durationYears: student.courseGroup.durationYears || 4,
              description: student.courseGroup.description,
              yearWiseFees: [], // Will be populated from API if available
            }
          : null,
        academicYear: student.academicYear || getCurrentAcademicYear(),
        batchYear: student.batchYear || getCurrentBatchYear(),
        enrollmentDate: student.enrollmentDate
          ? student.enrollmentDate.split("T")[0]
          : new Date().toISOString().split("T")[0],
        totalFee: student.totalFee || 0,
        finalFee: student.finalFee || 0,
      });

      // Load year-wise discounts for edit mode
      setYearWiseDiscounts(
        student.yearWiseDiscounts
          ? student.yearWiseDiscounts.map((d: any) => ({
              year: d.year,
              amount: d.amount?.toString() || "0",
              reason: d.reason || "",
            }))
          : []
      );
    } else {
      // Reset to empty form for add mode
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        dob: "",
        gender: "",
        fatherName: "",
        motherName: "",
        guardianName: "",
        aadharNumber: "",
        caste: "",
        category: "",
        religion: "",
        nationality: "",
        secondLanguageName: "",
        mole1: "",
        mole2: "",
        studentPhone: "",
        fatherPhone: "",
        motherPhone: "",
        guardianPhone: "",
        address: "",
        town: "",
        district: "",
        state: "",
        pincode: "",
        // Enhanced Academic Information
        intermediateGroup: "",
        intermediateGroupOther: "",
        intermediateBoard: "",
        intermediateBoardOther: "",
        intermediateMarks: "",
        intermediateMaxMarks: "",
        intermediatePercentage: "",
        intermediateYearOfPassing: "",
        intermediateAcademicYears: "",
        intermediateMediumOfInstruction: "",
        intermediateHallTicketNumber: "",
        intermediateCollege: "",
        // 10th Grade Information
        tenthBoard: "",
        tenthBoardOther: "",
        tenthMarks: "",
        tenthMaxMarks: "",
        tenthPercentage: "",
        tenthYearOfPassing: "",
        tenthAcademicYears: "",
        tenthMediumOfInstruction: "",
        tenthHallTicketNumber: "",
        medium: "",
        courseGroupId: "",
        selectedCourseGroup: null,
        academicYear: getCurrentAcademicYear(),
        batchYear: getCurrentBatchYear(),
        enrollmentDate: new Date().toISOString().split("T")[0],
        totalFee: 0,
        finalFee: 0,
      });

      // Reset year-wise discounts for add mode
      setYearWiseDiscounts([]);
    }
    setErrors([]);
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <DialogTitle>
            {isEditMode ? "Edit Student" : "Enroll New Student"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? `Update student information for ${student?.firstName} ${student?.lastName}.`
              : "Add a new student to the system with all required information and fee structure."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Error Display */}
            {errors.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 rounded-lg p-4"
              >
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-red-800 font-medium">
                      Please fix the following errors:
                    </h4>
                    <ul className="mt-2 text-red-700 text-sm list-disc list-inside space-y-1">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Auto-generated Credentials Info */}
            {/* <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-blue-50 border border-blue-200 rounded-lg p-4"
            >
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-blue-800 text-sm font-medium">Student Login Credentials</h4>
                  <div className="mt-2 text-blue-700 text-xs">
                    <p className="mb-2">Login credentials will be automatically generated:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Username:</strong> Generated from email address (part before @)</li>
                      <li><strong>Password:</strong> Default password "Student@123"</li>
                    </ul>
                    <p className="mt-2 text-xs text-blue-600">
                      💡 Students can change their password after first login
                    </p>
                  </div>
                </div>
              </div>
            </motion.div> */}

            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-x-2 border-b pb-2 ">
                <User className="h-4 w-4" />
                <span>Personal Information</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    placeholder="Enter last name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Father's Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.fatherName}
                    onChange={(e) =>
                      handleInputChange("fatherName", e.target.value)
                    }
                    placeholder="Enter father's name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Mother's Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.motherName}
                    onChange={(e) =>
                      handleInputChange("motherName", e.target.value)
                    }
                    placeholder="Enter mother's name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Guardian's Name
                  </label>
                  <Input
                    value={formData.guardianName}
                    onChange={(e) =>
                      handleInputChange("guardianName", e.target.value)
                    }
                    placeholder="Enter guardian's name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Aadhar Number
                  </label>
                  <Input
                    value={formData.aadharNumber}
                    onChange={(e) =>
                      handleInputChange("aadharNumber", e.target.value)
                    }
                    placeholder="1234-5678-9012"
                    maxLength={12}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="student@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="date"
                    value={formData.dob}
                    onChange={(e) => handleInputChange("dob", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Gender
                  </label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value) =>
                      handleInputChange("gender", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Caste
                  </label>
                  <Input
                    value={formData.caste}
                    onChange={(e) => handleInputChange("caste", e.target.value)}
                    placeholder="Enter caste"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Category
                  </label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) =>
                      handleInputChange("category", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="OBC">OBC</SelectItem>
                      <SelectItem value="SC">SC</SelectItem>
                      <SelectItem value="ST">ST</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Religion
                  </label>
                  <Input
                    value={formData.religion}
                    onChange={(e) =>
                      handleInputChange("religion", e.target.value)
                    }
                    placeholder="Enter religion"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Nationality
                  </label>
                  <Input
                    value={formData.nationality}
                    onChange={(e) =>
                      handleInputChange("nationality", e.target.value)
                    }
                    placeholder="Indian"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Second Language
                  </label>
                  <Input
                    value={formData.secondLanguageName}
                    onChange={(e) =>
                      handleInputChange("secondLanguageName", e.target.value)
                    }
                    placeholder="Hindi, Kannada, etc."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Identifying Mole 1
                  </label>
                  <Input
                    value={formData.mole1}
                    onChange={(e) => handleInputChange("mole1", e.target.value)}
                    placeholder="Left hand, right shoulder, etc."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Identifying Mole 2
                  </label>
                  <Input
                    value={formData.mole2}
                    onChange={(e) => handleInputChange("mole2", e.target.value)}
                    placeholder="Left hand, right shoulder, etc."
                  />
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 flex items-center gap-x-2">
                <Phone className="h-4 w-4" />
                <span>Contact Details</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Student Phone Number
                  </label>
                  <Input
                    value={formData.studentPhone}
                    onChange={(e) =>
                      handleInputChange("studentPhone", e.target.value)
                    }
                    placeholder="+91 9876543210"
                    maxLength={10}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Father Phone Number
                  </label>
                  <Input
                    value={formData.fatherPhone}
                    onChange={(e) =>
                      handleInputChange("fatherPhone", e.target.value)
                    }
                    placeholder="+91 9876543210"
                    maxLength={10}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Mother Phone Number
                  </label>
                  <Input
                    value={formData.motherPhone}
                    onChange={(e) =>
                      handleInputChange("motherPhone", e.target.value)
                    }
                    placeholder="+91 9876543210"
                    maxLength={10}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Guardian Phone Number
                  </label>
                  <Input
                    value={formData.guardianPhone}
                    onChange={(e) =>
                      handleInputChange("guardianPhone", e.target.value)
                    }
                    placeholder="+91 9876543210"
                    maxLength={10}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Address
                  </label>
                  <Input
                    value={formData.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    placeholder="Enter full address"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Town/City
                  </label>
                  <Input
                    value={formData.town}
                    onChange={(e) => handleInputChange("town", e.target.value)}
                    placeholder="Enter town/city"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    District
                  </label>
                  <Input
                    value={formData.district}
                    onChange={(e) =>
                      handleInputChange("district", e.target.value)
                    }
                    placeholder="Enter district"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    State
                  </label>
                  <Input
                    value={formData.state}
                    onChange={(e) => handleInputChange("state", e.target.value)}
                    placeholder="Enter state"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Pincode
                  </label>
                  <Input
                    value={formData.pincode}
                    onChange={(e) =>
                      handleInputChange("pincode", e.target.value)
                    }
                    placeholder="560001"
                    maxLength={6}
                  />
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 flex items-center space-x-2">
                <BookOpen className="h-4 w-4" />
                <span>Academic History</span>
              </h3>

              {/* Intermediate Education Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <BookOpen className="h-3 w-3" />
                  Intermediate (12th) Education
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Group
                    </label>
                    <Select
                      value={formData.intermediateGroup}
                      onValueChange={(value) => {
                        handleInputChange("intermediateGroup", value);
                        if (value !== "Others") {
                          handleInputChange("intermediateGroupOther", "");
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select group" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MPC">
                          MPC (Math, Physics, Chemistry)
                        </SelectItem>
                        <SelectItem value="BiPC">
                          BiPC (Biology, Physics, Chemistry)
                        </SelectItem>
                        <SelectItem value="CEC">
                          CEC (Civics, Economics, Commerce)
                        </SelectItem>
                        <SelectItem value="HEC">
                          HEC (History, Economics, Civics)
                        </SelectItem>
                        <SelectItem value="MEC">
                          MEC (Math, Economics, Commerce)
                        </SelectItem>
                        <SelectItem value="VOE">VOE (Vocational)</SelectItem>
                        <SelectItem value="Others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.intermediateGroup === "Others" && (
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-700">
                        Other Group
                      </label>
                      <Input
                        value={formData.intermediateGroupOther}
                        onChange={(e) =>
                          handleInputChange(
                            "intermediateGroupOther",
                            e.target.value
                          )
                        }
                        placeholder="Specify other group"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Board
                    </label>
                    <Select
                      value={formData.intermediateBoard}
                      onValueChange={(value) => {
                        handleInputChange("intermediateBoard", value);
                        if (value !== "Others") {
                          handleInputChange("intermediateBoardOther", "");
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select board" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BIE">
                          BIE (Board of Intermediate Education)
                        </SelectItem>
                        <SelectItem value="CBSE">CBSE</SelectItem>
                        <SelectItem value="ISSE">
                          ISSE (Indian School of Secondary Education)
                        </SelectItem>
                        <SelectItem value="APOSS">
                          APOSS (AP Open School Society)
                        </SelectItem>
                        <SelectItem value="NIOS">
                          NIOS (National Institute of Open Schooling)
                        </SelectItem>
                        <SelectItem value="Others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.intermediateBoard === "Others" && (
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-700">
                        Other Board
                      </label>
                      <Input
                        value={formData.intermediateBoardOther}
                        onChange={(e) =>
                          handleInputChange(
                            "intermediateBoardOther",
                            e.target.value
                          )
                        }
                        placeholder="Specify other board"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Marks Obtained
                    </label>
                    <Input
                      type="number"
                      value={formData.intermediateMarks}
                      onChange={(e) => {
                        handleInputChange("intermediateMarks", e.target.value);
                        // Auto-calculate percentage
                        const marks = parseFloat(e.target.value) || 0;
                        const maxMarks =
                          parseFloat(formData.intermediateMaxMarks) || 0;
                        if (marks > 0 && maxMarks > 0) {
                          const percentage = (marks / maxMarks) * 100;
                          handleInputChange(
                            "intermediatePercentage",
                            percentage.toFixed(2)
                          );
                        }
                      }}
                      placeholder="520"
                      min="0"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Maximum Marks
                    </label>
                    <Input
                      type="number"
                      value={formData.intermediateMaxMarks}
                      onChange={(e) => {
                        handleInputChange(
                          "intermediateMaxMarks",
                          e.target.value
                        );
                        // Auto-calculate percentage
                        const maxMarks = parseFloat(e.target.value) || 0;
                        const marks =
                          parseFloat(formData.intermediateMarks) || 0;
                        if (marks > 0 && maxMarks > 0) {
                          const percentage = (marks / maxMarks) * 100;
                          handleInputChange(
                            "intermediatePercentage",
                            percentage.toFixed(2)
                          );
                        }
                      }}
                      placeholder="600"
                      min="1"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Percentage
                    </label>
                    <Input
                      type="number"
                      value={formData.intermediatePercentage}
                      onChange={(e) =>
                        handleInputChange(
                          "intermediatePercentage",
                          e.target.value
                        )
                      }
                      placeholder="86.67"
                      min="0"
                      max="100"
                      step="0.01"
                      readOnly
                      className="bg-gray-50"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Year of Passing
                    </label>
                    <Input
                      type="number"
                      value={formData.intermediateYearOfPassing}
                      onChange={(e) =>
                        handleInputChange(
                          "intermediateYearOfPassing",
                          e.target.value
                        )
                      }
                      placeholder="2023"
                      min="2000"
                      max="2030"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Academic Years
                    </label>
                    <Input
                      value={formData.intermediateAcademicYears}
                      onChange={(e) =>
                        handleInputChange(
                          "intermediateAcademicYears",
                          e.target.value
                        )
                      }
                      placeholder="2021-2023"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Medium of Instruction
                    </label>
                    <Select
                      value={formData.intermediateMediumOfInstruction}
                      onValueChange={(value) =>
                        handleInputChange(
                          "intermediateMediumOfInstruction",
                          value
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select medium" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="English">English</SelectItem>
                        <SelectItem value="Hindi">Hindi</SelectItem>
                        <SelectItem value="Telugu">Telugu</SelectItem>
                        <SelectItem value="Kannada">Kannada</SelectItem>
                        <SelectItem value="Tamil">Tamil</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Hall Ticket Number
                    </label>
                    <Input
                      value={formData.intermediateHallTicketNumber}
                      onChange={(e) =>
                        handleInputChange(
                          "intermediateHallTicketNumber",
                          e.target.value
                        )
                      }
                      placeholder="1234567890"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-medium text-gray-700">
                      College Name
                    </label>
                    <Input
                      value={formData.intermediateCollege}
                      onChange={(e) =>
                        handleInputChange("intermediateCollege", e.target.value)
                      }
                      placeholder="Name of intermediate college"
                    />
                  </div>
                </div>
              </div>

              {/* 10th Grade Education Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <BookOpen className="h-3 w-3" />
                  10th Grade Education
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Board
                    </label>
                    <Select
                      value={formData.tenthBoard}
                      onValueChange={(value) => {
                        handleInputChange("tenthBoard", value);
                        if (value !== "Others") {
                          handleInputChange("tenthBoardOther", "");
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select board" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SSC">
                          SSC (Secondary School Certificate)
                        </SelectItem>
                        <SelectItem value="CBSE">CBSE</SelectItem>
                        <SelectItem value="ISSE">
                          ISSE (Indian School of Secondary Education)
                        </SelectItem>
                        <SelectItem value="APOSS">
                          APOSS (AP Open School Society)
                        </SelectItem>
                        <SelectItem value="NIOS">
                          NIOS (National Institute of Open Schooling)
                        </SelectItem>
                        <SelectItem value="Others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.tenthBoard === "Others" && (
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-700">
                        Other Board
                      </label>
                      <Input
                        value={formData.tenthBoardOther}
                        onChange={(e) =>
                          handleInputChange("tenthBoardOther", e.target.value)
                        }
                        placeholder="Specify other board"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Marks Obtained
                    </label>
                    <Input
                      type="number"
                      value={formData.tenthMarks}
                      onChange={(e) => {
                        handleInputChange("tenthMarks", e.target.value);
                        // Auto-calculate percentage
                        const marks = parseFloat(e.target.value) || 0;
                        const maxMarks =
                          parseFloat(formData.tenthMaxMarks) || 0;
                        if (marks > 0 && maxMarks > 0) {
                          const percentage = (marks / maxMarks) * 100;
                          handleInputChange(
                            "tenthPercentage",
                            percentage.toFixed(2)
                          );
                        }
                      }}
                      placeholder="450"
                      min="0"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Maximum Marks
                    </label>
                    <Input
                      type="number"
                      value={formData.tenthMaxMarks}
                      onChange={(e) => {
                        handleInputChange("tenthMaxMarks", e.target.value);
                        // Auto-calculate percentage
                        const maxMarks = parseFloat(e.target.value) || 0;
                        const marks = parseFloat(formData.tenthMarks) || 0;
                        if (marks > 0 && maxMarks > 0) {
                          const percentage = (marks / maxMarks) * 100;
                          handleInputChange(
                            "tenthPercentage",
                            percentage.toFixed(2)
                          );
                        }
                      }}
                      placeholder="500"
                      min="1"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Percentage
                    </label>
                    <Input
                      type="number"
                      value={formData.tenthPercentage}
                      onChange={(e) =>
                        handleInputChange("tenthPercentage", e.target.value)
                      }
                      placeholder="90.00"
                      min="0"
                      max="100"
                      step="0.01"
                      readOnly
                      className="bg-gray-50"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Year of Passing
                    </label>
                    <Input
                      type="number"
                      value={formData.tenthYearOfPassing}
                      onChange={(e) =>
                        handleInputChange("tenthYearOfPassing", e.target.value)
                      }
                      placeholder="2021"
                      min="2000"
                      max="2030"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Academic Years
                    </label>
                    <Input
                      value={formData.tenthAcademicYears}
                      onChange={(e) =>
                        handleInputChange("tenthAcademicYears", e.target.value)
                      }
                      placeholder="2020-2021"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Medium of Instruction
                    </label>
                    <Select
                      value={formData.tenthMediumOfInstruction}
                      onValueChange={(value) =>
                        handleInputChange("tenthMediumOfInstruction", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select medium" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="English">English</SelectItem>
                        <SelectItem value="Hindi">Hindi</SelectItem>
                        <SelectItem value="Telugu">Telugu</SelectItem>
                        <SelectItem value="Kannada">Kannada</SelectItem>
                        <SelectItem value="Tamil">Tamil</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Hall Ticket Number
                    </label>
                    <Input
                      value={formData.tenthHallTicketNumber}
                      onChange={(e) =>
                        handleInputChange(
                          "tenthHallTicketNumber",
                          e.target.value
                        )
                      }
                      placeholder="0987654321"
                    />
                  </div>
                </div>
              </div>

              {/* General Academic Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Primary Medium of Instruction
                    </label>
                    <Select
                      value={formData.medium}
                      onValueChange={(value) =>
                        handleInputChange("medium", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select medium" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="English">English</SelectItem>
                        <SelectItem value="Hindi">Hindi</SelectItem>
                        <SelectItem value="Telugu">Telugu</SelectItem>
                        <SelectItem value="Kannada">Kannada</SelectItem>
                        <SelectItem value="Tamil">Tamil</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Enrollment Details */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 flex items-center space-x-2">
                <GraduationCap className="h-4 w-4" />
                <span>Enrollment Details</span>
              </h3>

              {/* Course Group Selection */}
              <div className="space-y-3">
                <label className="text-xs font-medium text-gray-700">
                  Course Group <span className="text-red-500">*</span>
                </label>
                {loadingCourseGroups ? (
                  <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    <span className="ml-3 text-gray-600">
                      Loading course groups...
                    </span>
                  </div>
                ) : (
                  <Select
                    value={formData.courseGroupId}
                    onValueChange={handleCourseGroupChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a course group" />
                    </SelectTrigger>
                    <SelectContent>
                      {courseGroups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          <div className="flex items-center gap-4 justify-between w-full">
                            <div>
                              <div className="font-medium">{group.name}</div>
                              <div className="text-xs text-gray-500 text-left">
                                {group.code} • {group.durationYears} years
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-green-600">
                                {group.formattedFee}
                              </div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Enrollment Date
                  </label>
                  <Input
                    type="date"
                    value={formData.enrollmentDate}
                    onChange={(e) =>
                      handleInputChange("enrollmentDate", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Academic Year
                  </label>
                  <Input
                    value={formData.academicYear}
                    onChange={(e) =>
                      handleInputChange("academicYear", e.target.value)
                    }
                    placeholder="2024-25"
                  />
                  <p className="text-xs text-gray-500">
                    Format: YYYY-YY (auto-generated)
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    Batch Year
                  </label>
                  <Input
                    value={formData.batchYear}
                    onChange={(e) =>
                      handleInputChange("batchYear", e.target.value)
                    }
                    placeholder="2024"
                  />
                  <p className="text-xs text-gray-500">
                    Year of admission (auto-generated)
                  </p>
                </div>
              </div>
            </div>

            {/* Year-wise Fee Management */}
            {formData.selectedCourseGroup &&
              "yearWiseFees" in formData.selectedCourseGroup &&
              formData.selectedCourseGroup.yearWiseFees &&
              formData.selectedCourseGroup.yearWiseFees.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 flex items-center space-x-2">
                    <IndianRupee className="h-4 w-4" />
                    <span>Fee Management</span>
                  </h3>

                  {/* Year-wise Fee Breakdown Component */}
                  <YearwiseFeeBreakdown
                    courseGroup={
                      formData.selectedCourseGroup as CourseGroupForEnrollment
                    }
                    yearWiseDiscounts={yearWiseDiscounts}
                    showDiscountValidation={true}
                  />

                  {/* Year-wise Discount Input */}
                  <YearWiseDiscountInput
                    yearWiseFees={formData.selectedCourseGroup.yearWiseFees}
                    discounts={yearWiseDiscounts}
                    onChange={setYearWiseDiscounts}
                  />
                </motion.div>
              )}

            {/* Legacy Fee Management (for course groups without year-wise fees) */}
            {formData.selectedCourseGroup &&
              (!("yearWiseFees" in formData.selectedCourseGroup) ||
                !formData.selectedCourseGroup.yearWiseFees ||
                formData.selectedCourseGroup.yearWiseFees.length === 0) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 flex items-center space-x-2">
                    <IndianRupee className="h-4 w-4" />
                    <span>Fee Management</span>
                  </h3>

                  {/* Fee Summary Display */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-xs text-gray-600 font-medium">
                          Total Course Fee
                        </p>
                        <p className="text-xl font-bold text-green-600">
                          ₹{formData.totalFee.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-600 font-medium">
                          Final Fee
                        </p>
                        <p className="text-xl font-bold text-blue-600">
                          ₹{formData.finalFee.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 italic">
                    Note: This course group doesn't have year-wise fee structure
                    configured.
                  </p>
                </motion.div>
              )}
          </form>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-3 w-full justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={
                loading ||
                !formData.firstName ||
                !formData.lastName ||
                !formData.email ||
                !formData.courseGroupId
              }
            >
              {loading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                />
              ) : null}
              {loading
                ? isEditMode
                  ? "Updating Student..."
                  : "Creating Student..."
                : isEditMode
                ? "Update Student"
                : "Enroll Student"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddStudentDialog;
